/**
 * Help illustration registry (E13-F01-T03) — slug → bundled image URL.
 *
 * The 12 frames listed in the spec (§4.3) are shot by
 * `scripts/capture-help-screenshots.mjs` against the dev deploy and written to
 * `packages/web/src/assets/help/<slug>.png`. This module is the ONE place that
 * turns those files into URLs the section components can render.
 *
 * ── Why a glob and not 12 `import shot from '../assets/help/x.png'` lines.
 * A static import of a file that is not on disk is a BUILD error, and the
 * acceptance criteria require the opposite: «a section with no frame yet renders
 * without errors (no empty <figure>)». The capture script is explicitly allowed
 * to SKIP a frame when dev has no suitable data for it, so an absent PNG is a
 * normal state, not a broken build. `import.meta.glob(..., { eager: true })` is
 * still a fully static, build-time mechanism — Vite resolves and content-hashes
 * every matched asset exactly like a named import, and unmatched slugs simply
 * resolve to `undefined` here instead of failing the bundle.
 *
 * ── Why the slug list is hand-written rather than derived from the glob.
 * The union type is the contract the sections type-check against: a typo in
 * `<Shot slug="…" />` is a compile error, while a MISSING file is a runtime
 * `undefined` the `Shot` primitive renders as nothing. Deriving the type from
 * the glob would invert that — a missing file would become the compile error and
 * a typo would silently render nothing.
 */

/**
 * Every frame the guide can show, in capture order (spec §4.3 list).
 * A slug is part of the asset filename contract — renaming one means re-shooting
 * (`node scripts/capture-help-screenshots.mjs <slug>`).
 */
export const HELP_SHOT_SLUGS = [
  'login',
  'portfolio',
  'outlook-sidebar',
  'stage-stepper',
  'prekickoff',
  'kickoff',
  'generation',
  'document-modal',
  'regenerate',
  'launched',
  'inbox',
  'clients',
  // E13-F02 — Admin guide (shot by capture-help-admin-research-screenshots.mjs).
  // 'admin-prompts' and 'admin-ai-providers' were merged into 'admin-model-matrix'
  // when the two admin tabs merged into ModelMatrixTab.tsx (Task 15,
  // docs/superpowers/plans/2026-08-30-model-per-task-matrix.md).
  'admin-users',
  'admin-sourcing-teams',
  'admin-teams',
  'admin-model-matrix',
  'admin-address-book',
  'admin-position-types',
  'admin-questionnaire',
  'admin-research-settings',
  // E13-F03 — Research guide. 'research-publish' is deliberately absent: on dev
  // every research request currently sits in mandatory-review (gate) mode, so
  // the only safe way to reach the Publish screen (the auto-mode nav-only
  // review-publish button) has no request to click through — `review-submit`,
  // the gate-mode alternative, is a real mutation and the capture script never
  // clicks it. Add the slug once a suitable auto-mode request exists to shoot.
  'research-library',
  'research-intake',
  'research-run',
  'research-aichain',
  'research-review',
] as const;

export type HelpShotSlug = (typeof HELP_SHOT_SLUGS)[number];

/**
 * Build-time map of `../assets/help/<slug>.png` → emitted asset URL.
 * `query: '?url'` + `import: 'default'` gives the hashed URL string (the same
 * value a default import of the file would produce).
 */
const SHOT_MODULES = import.meta.glob('../assets/help/*.png', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const SHOT_URLS: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(SHOT_MODULES).map(([filePath, url]) => [
    filePath.slice(filePath.lastIndexOf('/') + 1).replace(/\.png$/, ''),
    url,
  ]),
);

/**
 * URL of a captured frame, or `undefined` when that frame has not been shot yet
 * (the section then renders text-only — see `Shot` in `HelpPrimitives.tsx`).
 */
export function helpShotUrl(slug: HelpShotSlug): string | undefined {
  return SHOT_URLS[slug];
}

/** Slugs that currently have a file on disk — used by the unit test / tooling. */
export function availableHelpShots(): readonly HelpShotSlug[] {
  return HELP_SHOT_SLUGS.filter((slug) => SHOT_URLS[slug] !== undefined);
}
